import React, { useEffect, useState, createRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useIntl, defineMessages } from 'react-intl';

import {
  Button,
  Pagination,
  Icon as SemanticIcon,
  Input,
  Dropdown,
  Grid,
} from 'semantic-ui-react';
import { motion, AnimatePresence } from 'framer-motion';

import { searchContent, resetSearchContent } from '@plone/volto/actions';

import { flattenToAppURL } from '@plone/volto/helpers';
import { Icon } from '@plone/volto/components';
import paginationLeftSVG from '@plone/volto/icons/left-key.svg';
import paginationRightSVG from '@plone/volto/icons/right-key.svg';
import { VariationWrapper } from 'iovisit-theme/components';
import Card from 'iovisit-theme/components/Blocks/Listing/CardsTemplate/Card';
import { getSearchFiltersArtworks } from 'iovisit-museum-theme/actions';

const messages = defineMessages({
  searchText: {
    id: 'artworks_search_text',
    defaultMessage: 'Digita la parola chiave',
  },
  clearText: {
    id: 'clear_text',
    defaultMessage: 'Cancella',
  },
  loading: {
    id: 'loading',
    defaultMessage: 'Caricamento in corso..',
  },
  all: {
    id: 'all',
    defaultMessage: 'Tutti',
  },
  results_found: { id: 'results_found', defaultMessage: 'risultati' },
  no_results_found: {
    id: 'no_results_found',
    defaultMessage: 'Nessun risultato corrispondente ai criteri di ricerca',
  },
  search_name: {
    id: 'search_name',
    defaultMessage: 'Digita il nome',
  },
  search_author: {
    id: 'artworks_search_author',
    defaultMessage: "Scegli l'autore",
  },
  search_period: {
    id: 'artworks_search_period',
    defaultMessage: 'Scegli il periodo',
  },
  search_typology: {
    id: 'artworks_search_typology',
    defaultMessage: 'Scegli la tipologia',
  },
  reset_search: {
    id: 'reset_search',
    defaultMessage: 'Annulla ricerca',
  },
});

const FILTERS_CONFIG = {
  name: { type: 'text' },
  author: { type: 'multiselect' },
  period: { type: 'select' },
  typology: { type: 'multiselect' },
};

const FILTERS_FIELDS = ['name', 'author', 'period', 'typology'];
const B_SIZE = 10;
/**
 * View class.
 * @class View
 * @extends Component
 */
const View = ({ data, isEditMode, state }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const museum_id = flattenToAppURL(data.museum?.[0]?.['@id']) ?? '';
  const subrequest = museum_id + '_search_artworks';
  const resultsRef = createRef();

  const searchFilters = useSelector((state) => {
    return state.searchFiltersArtworks.subrequests?.[subrequest] || {};
  });

  const searchState = useSelector((state) => {
    return state.search?.subrequests?.[subrequest] ?? {};
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});

  const doSearch = (page = currentPage) => {
    if (Object.keys(filters).length === 0) {
      return;
    }
    const search_filters = Object.entries(filters).reduce(
      (a, [k, v]) => (v == null ? a : ((a[k] = v), a)),
      {},
    );
    delete search_filters.name;

    if (filters.name) {
      search_filters.SearchableText = filters.name + '*';
    }

    search_filters['Language'] = intl.locale;
    search_filters['portal_type'] = 'Artwork';

    if (!searchState.loading) {
      const searchOptions = {
        fullobjects: true,
        ...search_filters,
        b_size: B_SIZE,
        b_start: (page - 1) * B_SIZE, //(pager.page - 1) * config.settings.defaultPageSize,
        //sort_on: 'title',
      };

      dispatch(searchContent(museum_id, searchOptions, subrequest));
    }
  };

  //get filters
  useEffect(() => {
    if (!searchFilters.loading && !searchFilters.loaded) {
      dispatch(getSearchFiltersArtworks(museum_id, subrequest));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [museum_id]);

  //clear filters if data (block props) changes
  useEffect(() => {
    setFilters({});
  }, [data]);

  useEffect(() => {
    let applyedFiltersKeys = Object.keys(filters ?? [])?.filter(
      (item) => filters[item]?.length > 0,
    );

    if (applyedFiltersKeys?.length === 0) {
      setCurrentPage(1);
      dispatch(resetSearchContent(subrequest));
    } else {
      if (currentPage > 1) {
        setCurrentPage(1);
      }
      doSearch(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const filterChange = (field, value) => {
    var f = { ...filters, [field]: value };
    if (typeof value === 'string' && value.length === 0) {
      delete f[field];
    }
    setFilters(f);
  };

  const handleQueryPaginationChange = (e, { activePage }) => {
    resultsRef.current.scrollIntoView({ behavior: 'smooth' });
    const current = activePage ?? 1;

    setCurrentPage(current);
    doSearch(current);
  };

  const drawFilters = () => {
    return (
      <div className={'iv--search-filters search_type_' + data.search_type}>
        {FILTERS_FIELDS.map((field) => {
          switch (FILTERS_CONFIG[field].type) {
            case 'text':
              return (
                <div className="iv--filter">
                  <div className="iv--search-filter-text" key={field}>
                    <Input
                      placeholder={intl.formatMessage(
                        messages['search_' + field],
                      )}
                      aria-label={intl.formatMessage(
                        messages['search_' + field],
                      )}
                      value={filters[field] || ''}
                      icon
                      onChange={(e, data) => {
                        filterChange(field, data.value);
                      }}
                      aria-controls="search-results-region-sr"
                    >
                      <input />
                      {filters[field]?.length > 0 && (
                        <Button
                          inverted
                          icon
                          title={intl.formatMessage(messages.clearText)}
                          aria-label={intl.formatMessage(messages.clearText)}
                          onClick={() => {
                            filterChange(field, '');
                          }}
                        >
                          <SemanticIcon name="delete" />
                        </Button>
                      )}
                    </Input>
                  </div>
                </div>
              );
            case 'select':
              const select_values = searchFilters?.data?.indexes?.[field] ?? [];

              return select_values?.length === 0 ? (
                <></>
              ) : (
                <div className="iv--filter">
                  <Dropdown
                    placeholder={intl.formatMessage(
                      messages['search_' + field],
                    )}
                    aria-label={intl.formatMessage(messages['search_' + field])}
                    className="iv--search-filter-select"
                    clearable
                    search
                    selection
                    options={[
                      ...select_values.map((f) => ({
                        key: f.value,
                        value: f.value,
                        text: f.label,
                      })),
                      {
                        key: 'all',
                        value: null,
                        text: intl.formatMessage(messages.all),
                      },
                    ]}
                    value={filters[field] || ''}
                    onChange={
                      (e, data) => {
                        filterChange(field, data.value);
                      }
                      // dispatchFilter({
                      //   type: filtersActions.SUBJECT,
                      //   value: data.value,
                      // })
                    }
                    key={field}
                    aria-controls="search-results-region-sr"
                  />
                </div>
              );

            case 'multiselect':
              const multiselect_values =
                searchFilters?.data?.indexes?.[field] ?? [];
              return multiselect_values?.length === 0 ? (
                <></>
              ) : (
                <div className="iv--filter">
                  <Dropdown
                    placeholder={intl.formatMessage(
                      messages['search_' + field],
                    )}
                    aria-label={intl.formatMessage(messages['search_' + field])}
                    className="iv--search-filter-select"
                    multiple
                    search
                    selection
                    options={multiselect_values?.map((f) => ({
                      key: f.value,
                      value: f.value,
                      text: f.label,
                    }))}
                    value={filters[field] || []}
                    onChange={(e, data) => {
                      filterChange(field, data.value);
                    }}
                    key={field}
                    aria-controls="search-results-region-sr"
                  />
                </div>
              );
            default:
              return null;
          }
        })}
        {/* <div className="iv--search-filter-reset iv--filter">
          <Button className="reset-filters" onClick={() => setFilters({})}>
            {intl.formatMessage(messages.reset_search)}
          </Button>
        </div> */}
      </div>
    );
  };

  return (
    <div
      className={data['@type']}
      id="search-results-region-sr"
      ref={resultsRef}
    >
      {searchFilters.data?.indexes ? (
        drawFilters()
      ) : (
        <div className="loading">{intl.formatMessage(messages.loading)}</div>
      )}

      <div id="result-list">
        {filters && Object.keys(filters).length > 0 && (
          <div className="iv--results-count">
            {searchState.total > 0
              ? `${searchState.total} ${intl.formatMessage(
                  messages.results_found,
                )}`
              : intl.formatMessage(messages.no_results_found)}
          </div>
        )}
        {searchState.items?.length > 0 && (
          <>
            <AnimatePresence initial={false}>
              <div className="items-list block listing">
                <VariationWrapper items={searchState.items} variation="cards">
                  <Grid stackable>
                    {searchState.items.map((item) => {
                      return (
                        <Grid.Column
                          tablet={12}
                          computer={6}
                          widescreen={4}
                          largeScreen={4}
                          key={item.UID}
                        >
                          <motion.div
                            className="item"
                            initial={{ scale: 0.85, opacity: 0.1 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.7, opacity: 0.2 }}
                          >
                            <Card
                              item={item}
                              show_icon={false}
                              show_thematic_area={false}
                              show_item_description={false}
                              image_size="s"
                              isEditMode={isEditMode}
                            />
                          </motion.div>
                        </Grid.Column>
                      );
                    })}
                  </Grid>
                </VariationWrapper>
              </div>
            </AnimatePresence>
            {searchState.total > B_SIZE && (
              <div className="pagination-wrapper">
                <Pagination
                  activePage={currentPage}
                  totalPages={Math.ceil(searchState.total / B_SIZE)}
                  onPageChange={handleQueryPaginationChange}
                  firstItem={null}
                  lastItem={null}
                  prevItem={{
                    content: <Icon name={paginationLeftSVG} size="18px" />,
                    icon: true,
                    'aria-disabled': currentPage === 1,
                    className: currentPage === 1 ? 'disabled' : null,
                  }}
                  nextItem={{
                    content: <Icon name={paginationRightSVG} size="18px" />,
                    icon: true,
                    'aria-disabled': currentPage * B_SIZE >= searchState.total,
                    className:
                      currentPage * B_SIZE >= searchState.total
                        ? 'disabled'
                        : null,
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
View.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default View;
